import { useI18n } from '@/i18n'
import { useOnAuthBehaviour } from '@/stores/auth'
import { FileSystemEntry } from '@/types'
import { APIConsume, matchesSearch, useRetrieveHelpers, useStoreHelpers } from '@/util'
import { APIObject, APIStoreOptions, URLBuilder } from '@rokoli/bnb'
import {
  APICreate,
  APIListPaginated,
  APIRemove,
  APIRetrieve,
  APIUpdate,
  createExtendableAPI,
} from '@rokoli/bnb/drf'
import { defineStore } from 'pinia'
import { computed, ref, Ref, shallowRef, toValue, watch } from 'vue'

export function createBaseAPIStore<T extends APIObject>(
  storeId: string,
  endpoint: URLBuilder,
  options?: APIStoreOptions,
  behaviour?: { syncOnAuth?: boolean; syncOnLocaleChange?: boolean },
) {
  return defineStore(storeId, () => {
    const { api, base } = createExtendableAPI<T>(endpoint, options)
    const listOperations = APIListPaginated(api)
    const consumeOperations = APIConsume(listOperations.listIsolated)
    const retrieveOperations = APIRetrieve(api)
    const { locale } = useI18n()

    function updateCache() {
      const headers: Record<string, string> = behaviour?.syncOnLocaleChange
        ? { 'Accept-Language': `${locale.value}, *` }
        : {}
      return consumeOperations.consume({ requestInit: { headers } })
    }

    const isSynced = behaviour?.syncOnAuth
      ? useOnAuthBehaviour(updateCache).done
      : Promise.resolve(true)

    if (behaviour?.syncOnLocaleChange) {
      watch(locale, updateCache)
    }

    return {
      isSynced,
      ...base,
      ...listOperations,
      ...consumeOperations,
      ...retrieveOperations,
      ...APICreate(api),
      ...APIUpdate(api),
      ...APIRemove(api),
      ...useRetrieveHelpers({ ...base, ...retrieveOperations }),
    }
  })
}

export function createFSIOStore(storeId: string, endpoint: URLBuilder, options?: APIStoreOptions) {
  return defineStore(storeId, () => {
    const { api, base } = createExtendableAPI<FileSystemEntry>(endpoint, options)
    const listOperations = APIListPaginated(api)
    const retrieveOperations = APIRetrieve(api)

    function useSearch(term: Ref<string>) {
      const isLoadingRemoteResults = ref(false)
      const remoteResults = shallowRef<FileSystemEntry[]>([])
      watch(term, async (term) => {
        isLoadingRemoteResults.value = true
        try {
          const result = await listOperations.listIsolated(1, {
            limit: 50,
            query: new URLSearchParams({ search: term }),
          })
          remoteResults.value = result.items
        } finally {
          isLoadingRemoteResults.value = false
        }
      })

      const localResults = computed(() => {
        const _term = toValue(term)
        return base.items.value.filter(
          (item) => matchesSearch(item.path, _term) || matchesSearch(item.label, _term),
        )
      })

      return computed(() => {
        if (isLoadingRemoteResults.value) return localResults.value
        return remoteResults.value
      })
    }

    function useAutoComplete(term: Ref<string>) {
      const entries = useSearch(term)
      return computed(() => entries.value.map((entry) => entry.path))
    }

    return {
      ...base,
      ...listOperations,
      ...retrieveOperations,
      ...useStoreHelpers({ ...base, ...retrieveOperations, ...listOperations }),
      useSearch,
      useAutoComplete,
    }
  })
}
